import { z } from "zod";

import {
  busbarEvidenceStatusSchema,
  busbarEvidenceWarningCodeSchema,
  busbarKindSchema,
  busbarMaterialSchema,
  faultTypeSchema,
  prospectiveCurrentSourceSchema,
  shortCircuitCaseSchema,
} from "./busbarEvidenceContract";
import { voltageDropBasisSchema } from "./busbarSelectionContract";
import { calculationRunSummarySchema } from "./calculationRun";
import { jurisdictionProfileSchema } from "./cableContract";
import { exactDecimalSchema } from "./faultContract";
import { CHOOSE_A_FAULT_RUN, formatApiError, requestSignal } from "./faultLink";
import { ApiError } from "./http";

const optionalRequestText = (maxLength: number) =>
  z.string().trim().max(maxLength).nullable().optional();

const RUNS_URL = "/api/v1/electrical/busbar-evidence/runs";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// A fault level typed by the engineer: allowed, but not linked evidence, so the study
// is REVIEW_REQUIRED (A24 (c)). Without the peak ip the peak check is not made.
export const typedProspectiveCurrentSchema = z
  .object({
    prospective_current_ka: exactDecimalSchema,
    peak_current_ka: exactDecimalSchema.optional(),
    system_nominal_voltage_v: exactDecimalSchema,
    fault_bus_code: z.string().trim().min(1).max(50),
  })
  .strict();

// The neutral bar pair (A24 (e)): both figures, or the block left out. Whether harmonic data is
// held (KES-EL-BUS-002 Rev 0 section 11) is true, false or left out (not stated).
export const neutralBarRequestSchema = z
  .object({
    design_neutral_current_a: exactDecimalSchema,
    declared_neutral_rating_a: exactDecimalSchema,
    harmonic_data_available: z.boolean().optional(),
  })
  .strict();

// The trunking route and its declared voltage drop (A24 (f)); the basis of the declared figure
// is left out when it is not stated (the drop is then not calculated).
export const trunkingRequestSchema = z
  .object({
    route_length_m: exactDecimalSchema,
    declared_voltage_drop_mv_per_a_m: exactDecimalSchema,
    allowable_voltage_drop_percent: exactDecimalSchema,
    voltage_drop_basis: voltageDropBasisSchema.optional(),
  })
  .strict();

/** The choices a study needs; the backend imposes no default (A24 (b)). */
export const REQUIRED_STUDY_CHOICES = ["kind", "material"] as const;

// An unmade choice is reported as a missing value, so the form says "<Label> is required."
function requireChoice(field: string, value: unknown, ctx: z.RefinementCtx): void {
  if (value === undefined) {
    ctx.addIssue({
      code: "invalid_type",
      expected: "string",
      received: "undefined",
      message: `${field} is required`,
      path: [field],
    });
  }
}

// Every rating is the panel builder's declared figure (A24 (b), (d)); nothing is
// defaulted. An ambient is a temperature: any exact decimal, zero and below included.
export const busbarEvidenceRequestSchema = z
  .object({
    code: z.string().trim().min(1).max(50),
    name: z.string().trim().min(1).max(200),
    bus_code: z.string().trim().min(1).max(50),
    // Exactly one of the two: a stored EOS-04 fault run of the organization, or a typed
    // value. The rule below reports the pair the same way the backend does, and names a
    // blank or malformed run id with the selector's own sentence.
    fault_run_id: z.string().optional(),
    typed: typedProspectiveCurrentSchema.optional(),
    // The choices are optional here only so a missing one is reported by the rule below
    // as a missing value ("... is required."), the way the breaker form reports its
    // choices; the backend requires both of them.
    kind: busbarKindSchema.optional(),
    material: busbarMaterialSchema.optional(),
    design_current_a: exactDecimalSchema,
    declared_rated_current_a: exactDecimalSchema,
    reference_ambient_c: exactDecimalSchema,
    site_ambient_c: exactDecimalSchema,
    short_time_withstand_current_ka: exactDecimalSchema,
    short_time_withstand_duration_s: exactDecimalSchema,
    required_fault_duration_s: exactDecimalSchema,
    peak_withstand_current_ka: exactDecimalSchema,
    rated_operational_voltage_v: exactDecimalSchema,
    neutral: neutralBarRequestSchema.optional(),
    trunking: trunkingRequestSchema.optional(),
    jurisdiction_profile: jurisdictionProfileSchema.optional(),
    notes: optionalRequestText(1000),
  })
  .strict()
  .superRefine((values, ctx) => {
    for (const field of REQUIRED_STUDY_CHOICES) {
      requireChoice(field, values[field], ctx);
    }

    if ((values.fault_run_id === undefined) === (values.typed === undefined)) {
      ctx.addIssue({
        code: "custom",
        message: "give exactly one prospective current source: fault_run_id or typed",
        path: ["fault_run_id"],
      });
    } else if (values.fault_run_id !== undefined && !UUID.test(values.fault_run_id)) {
      ctx.addIssue({ code: "custom", message: CHOOSE_A_FAULT_RUN, path: ["fault_run_id"] });
    }

    // Mirrors the backend rule (schemas/busbar_evidence.py, validate_source_and_kind).
    if (values.kind === "BUSBAR_TRUNKING" && values.trunking === undefined) {
      ctx.addIssue({
        code: "custom",
        message: "trunking is required for a BUSBAR_TRUNKING study",
        path: ["trunking"],
      });
    }
    if (values.kind === "ASSEMBLY_BUSBAR" && values.trunking !== undefined) {
      ctx.addIssue({
        code: "custom",
        message: "trunking belongs to a BUSBAR_TRUNKING study only",
        path: ["trunking"],
      });
    }
  });

export const busbarEvidenceWarningSchema = z
  .object({
    code: busbarEvidenceWarningCodeSchema,
    message: z.string(),
  })
  .strict();

// F1 (2026-10-15): the three fields the 07a neutral rule and the trunking basis added (engine
// 0.2.0, commit 9c8b0b8) are optional for a STORED result - a run of engine 0.1.0 never recorded
// them and stored snapshots are immutable evidence. Absent stays undefined (not null: null is
// the engine's own "not stated" / "not decided") and is shown as "Not recorded", never as a
// value. Every fresh result of the current engine carries all three (the backend proves it).
export const neutralBarResponseSchema = z
  .object({
    design_neutral_current_a: z.string(),
    declared_neutral_rating_a: z.string(),
    harmonic_data_available: z.boolean().nullable().optional(),
  })
  .strict();

export const trunkingResponseSchema = z
  .object({
    route_length_m: z.string(),
    declared_voltage_drop_mv_per_a_m: z.string(),
    allowable_voltage_drop_percent: z.string(),
    voltage_drop_basis: voltageDropBasisSchema.nullable().optional(),
  })
  .strict();

// Every input echoed, each derived figure the engine's, each check true / false / null
// (not made - no peak current, no neutral pair, no trunking block).
export const busbarEvidenceResponseSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    bus_code: z.string(),
    current_source: prospectiveCurrentSourceSchema,
    // The linked run, its case, fault type and bus exactly as the stored result holds
    // them; null run / case / type for a typed value. ip is null when it was not held.
    fault_run_id: z.string().uuid().nullable(),
    calculation_case: shortCircuitCaseSchema.nullable(),
    fault_type: faultTypeSchema.nullable(),
    fault_bus_code: z.string(),
    prospective_current_ka: z.string(),
    peak_current_ka: z.string().nullable(),
    system_nominal_voltage_v: z.string(),
    kind: busbarKindSchema,
    material: busbarMaterialSchema,
    design_current_a: z.string(),
    declared_rated_current_a: z.string(),
    reference_ambient_c: z.string(),
    site_ambient_c: z.string(),
    short_time_withstand_current_ka: z.string(),
    short_time_withstand_duration_s: z.string(),
    required_fault_duration_s: z.string(),
    peak_withstand_current_ka: z.string(),
    rated_operational_voltage_v: z.string(),
    neutral: neutralBarResponseSchema.nullable(),
    trunking: trunkingResponseSchema.nullable(),
    loading_percent: z.string(),
    // Rating minus the fault level (negative when the check failed); null when not made.
    icw_margin_ka: z.string(),
    ipk_margin_ka: z.string().nullable(),
    neutral_loading_percent: z.string().nullable(),
    voltage_drop_v: z.string().nullable(),
    voltage_drop_percent: z.string().nullable(),
    rated_current_adequate: z.boolean(),
    ambient_within_reference: z.boolean(),
    icw_adequate: z.boolean(),
    duration_within_icw: z.boolean(),
    ipk_adequate: z.boolean().nullable(),
    voltage_adequate: z.boolean(),
    neutral_adequate: z.boolean().nullable(),
    // True when the neutral is rated below the phase and no harmonic data is stated: a review.
    neutral_review_required: z.boolean().nullable().optional(),
    voltage_drop_adequate: z.boolean().nullable(),
    status: busbarEvidenceStatusSchema,
    warnings: z.array(busbarEvidenceWarningSchema),
    jurisdiction_profile: jurisdictionProfileSchema,
    notes: z.string().nullable(),
  })
  .strict();

export const busbarEvidenceRunCreateRequestSchema = z
  .object({
    study: busbarEvidenceRequestSchema,
    project_revision_id: z.string().uuid().optional(),
    notes: optionalRequestText(2000),
  })
  .strict();

export const busbarEvidenceRunResponseSchema = z
  .object({
    run: calculationRunSummarySchema,
    result: busbarEvidenceResponseSchema,
  })
  .strict();

export type TypedProspectiveCurrent = z.infer<typeof typedProspectiveCurrentSchema>;
export type NeutralBarRequest = z.infer<typeof neutralBarRequestSchema>;
export type TrunkingRequest = z.infer<typeof trunkingRequestSchema>;
export type BusbarEvidenceRequest = z.infer<typeof busbarEvidenceRequestSchema>;
export type BusbarEvidenceWarning = z.infer<typeof busbarEvidenceWarningSchema>;
export type BusbarEvidenceResponse = z.infer<typeof busbarEvidenceResponseSchema>;
export type BusbarEvidenceRunCreateRequest = z.infer<typeof busbarEvidenceRunCreateRequestSchema>;
export type BusbarEvidenceRunResponse = z.infer<typeof busbarEvidenceRunResponseSchema>;

/**
 * Calculate a busbar evidence study and persist it as a run revision.
 *
 * The request carries the whole run body, because a study's notes belong to the
 * run, not to the checks the engine makes. With a project revision id the run is
 * stored under that revision of the selected project - and a linked fault run must
 * then belong to the same revision; without one it belongs to no project. An
 * unchanged study returns the stored revision with HTTP 200 instead of a new one
 * (A11); both are valid here.
 */
export async function createBusbarEvidenceRun(
  payload: BusbarEvidenceRunCreateRequest,
  signal?: AbortSignal,
  projectRevisionId?: string,
): Promise<BusbarEvidenceRunResponse> {
  const validatedPayload = busbarEvidenceRunCreateRequestSchema.parse(payload);

  const response = await fetch(RUNS_URL, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(
      projectRevisionId === undefined
        ? validatedPayload
        : { ...validatedPayload, project_revision_id: projectRevisionId },
    ),
    cache: "no-store",
    signal: requestSignal(signal),
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(formatApiError(data, response.status, "Busbar evidence"), response.status);
  }

  const parsed = busbarEvidenceRunResponseSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error("Unexpected response from the KES Electrical OS busbar evidence runs API.");
  }

  return parsed.data;
}
